import { cn } from '@/lib/utils';
import type { Severity } from './testOptimizationUtils';
import { buildDiffCardClass } from '@/components/smartai/ComponentBuildDiff/build-diff-layout';
import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';

/* ── Shell / page ───────────────────────────────────────────────── */

export const testOptRootClass =
  'flex h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)] flex-col overflow-hidden bg-slate-50';

export const testOptRootEmbeddedClass =
  'flex h-[min(72vh,720px)] max-h-[min(72vh,720px)] flex-col overflow-hidden rounded-md border border-slate-200 bg-slate-50';

export const testOptPageClass = onboardingPageClass;

export const testOptEmptyCardClass = cn(buildDiffCardClass, 'm-4');

export const testOptTopbarClass =
  'flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-2.5';

export const testOptTitleClass =
  'm-0 text-[1.1rem] font-semibold text-slate-900';

export const testOptScanIdClass =
  'mt-0.5 mb-0 text-[0.75rem] text-slate-500 [&_code]:text-[0.7rem]';

export const testOptBodyClass =
  'flex min-h-0 flex-1 flex-col overflow-hidden';

export const testOptPrimaryTabsClass =
  'flex shrink-0 gap-0 border-b border-slate-700 bg-slate-900 px-2';

export const testOptPrimaryTabClass =
  'inline-flex min-h-11 cursor-pointer items-center justify-center gap-1.5 border-0 border-b-[3px] border-transparent bg-transparent px-5 py-2 text-[0.85rem] font-semibold text-slate-400 transition-[color,border-color,background] duration-150 hover:bg-white/[0.04] hover:text-slate-200';

export const testOptPrimaryTabActiveClass =
  'border-orange-600 bg-orange-600/10 text-orange-400';

export const testOptPrimaryTabCountClass =
  'text-[0.75rem] font-medium text-slate-500';

export const testOptPrimaryTabCountActiveClass = 'text-orange-300';

export function testOptPrimaryTab(active: boolean): string {
  return cn(testOptPrimaryTabClass, active && testOptPrimaryTabActiveClass);
}

export function testOptPrimaryTabCount(active: boolean): string {
  return cn(
    testOptPrimaryTabCountClass,
    active && testOptPrimaryTabCountActiveClass,
  );
}

export const testOptGraphEmbedClass =
  'flex min-h-0 flex-1 flex-col overflow-hidden';

export const testOptGraphEmbedLoadingClass =
  'flex flex-1 items-center justify-center p-8';

export const testOptPlaceholderClass =
  'flex flex-1 items-center justify-center overflow-auto p-8 text-center text-slate-500';

export const testOptPlaceholderErrorClass = cn(
  testOptPlaceholderClass,
  'text-red-700',
);

/* ── Methods tab ────────────────────────────────────────────────── */

export const testOptMethodsClass =
  'flex min-h-0 flex-1 flex-col overflow-hidden';

export const testOptMethodsEmbeddedClass = 'min-h-0';

export const testOptMethodsSourceHintClass =
  'm-0 border-b border-slate-200 bg-slate-100 px-4 py-2 text-[0.75rem] text-slate-600 [&_code]:text-[0.7rem]';

export const testOptMethodsToolbarClass =
  'flex shrink-0 flex-col gap-3 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between';

export const testOptMethodsToolbarFiltersClass =
  'flex min-w-0 flex-1 flex-wrap items-center gap-2.5';

export const testOptMethodsFilterGroupClass =
  'inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] p-0.5 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]';

export const testOptFilterClass =
  'inline-flex !h-auto cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-transparent px-2.5 py-1.5 text-[0.8125rem] font-medium text-[var(--inv-text-secondary)] shadow-none transition-[color,background-color,box-shadow] duration-150 hover:!translate-y-0 hover:bg-[var(--inv-surface)] hover:text-[var(--inv-text)] hover:shadow-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring,rgba(0,70,184,0.14))]';

export const testOptFilterActiveClass =
  'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))] hover:!bg-[var(--inv-surface)] hover:text-[var(--inv-accent)]';

const changeFilterActive: Record<'added' | 'modified' | 'deleted', string> = {
  added:
    'bg-[color-mix(in_srgb,#16a34a_10%,var(--inv-surface))] text-emerald-800 hover:!text-emerald-800',
  modified:
    'bg-[color-mix(in_srgb,#d97706_10%,var(--inv-surface))] text-amber-900 hover:!text-amber-900',
  deleted:
    'bg-[color-mix(in_srgb,#dc2626_10%,var(--inv-surface))] text-red-800 hover:!text-red-800',
};

export function testOptFilterClassName(
  active: boolean,
  changeKey?: 'added' | 'modified' | 'deleted',
): string {
  if (!active) return testOptFilterClass;
  if (changeKey) {
    return cn(testOptFilterClass, changeFilterActive[changeKey]);
  }
  return cn(testOptFilterClass, testOptFilterActiveClass);
}

export const testOptFilterCountBadgeClass =
  'inline-flex min-w-[1.125rem] items-center justify-center rounded-md px-1.5 py-0.5 text-[0.6875rem] font-semibold tabular-nums leading-none';

export function testOptFilterCountBadge(
  active: boolean,
  changeKey?: 'added' | 'modified' | 'deleted',
): string {
  if (!active) {
    return cn(
      testOptFilterCountBadgeClass,
      'bg-[var(--inv-surface-muted)] text-[var(--inv-text-muted)]',
    );
  }
  if (changeKey === 'added') {
    return cn(testOptFilterCountBadgeClass, 'bg-emerald-100/90 text-emerald-800');
  }
  if (changeKey === 'modified') {
    return cn(testOptFilterCountBadgeClass, 'bg-amber-100/90 text-amber-900');
  }
  if (changeKey === 'deleted') {
    return cn(testOptFilterCountBadgeClass, 'bg-red-100/90 text-red-800');
  }
  return cn(
    testOptFilterCountBadgeClass,
    'bg-[color-mix(in_srgb,var(--inv-accent)_12%,transparent)] text-[var(--inv-accent)]',
  );
}

export const testOptMethodsSearchWrapClass =
  'relative w-full shrink-0 sm:w-auto sm:min-w-[min(100%,16.5rem)]';

export const testOptMethodsSearchIconClass =
  'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--inv-text-muted)]';

export const testOptMethodsSearchClass =
  'box-border h-9 w-full rounded-lg border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface)] py-2 pl-9 pr-3 text-[0.8125rem] text-[var(--inv-text)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))] placeholder:text-[var(--inv-text-muted)] transition-[border-color,box-shadow] duration-150 focus:border-[var(--inv-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--inv-accent-ring,rgba(0,70,184,0.14))]';

export const testOptMethodsMainClass = 'flex flex-1 overflow-hidden';

export const testOptMethodsTableWrapClass =
  'flex-1 overflow-auto bg-white';

/* ── Methods table ──────────────────────────────────────────────── */

export const testOptTableClass =
  'w-full min-w-[64rem] table-fixed border-collapse text-[0.8rem]';

export const testOptColCheckClass = 'w-10';
export const testOptColMethodClass = 'w-44';
export const testOptColFileClass = 'w-56';
export const testOptColChangeClass = 'w-[6.25rem]';
export const testOptColSeverityClass = 'w-[6.5rem]';
export const testOptColScoreClass = 'w-[4.25rem]';
export const testOptColFanClass = 'w-[4.75rem]';
export const testOptColCoveredClass = 'w-72 min-w-72';

export const testOptThClass =
  'sticky top-0 z-[2] overflow-hidden text-ellipsis whitespace-nowrap border-b border-slate-200 bg-slate-100 px-3 py-2.5 text-left font-semibold text-slate-600 shadow-[0_1px_0_#e2e8f0]';

export const testOptThCheckClass =
  'sticky top-0 z-[2] w-10 overflow-visible border-b border-slate-200 bg-slate-100 px-2 py-2.5 text-center font-semibold text-slate-600 shadow-[0_1px_0_#e2e8f0]';

export const testOptTdClass =
  'min-w-0 overflow-hidden border-b border-slate-100 px-3 py-2 align-top';

export const testOptTdCheckClass =
  'w-10 overflow-visible border-b border-slate-100 px-2 py-2 text-center align-top';

export const testOptTableMethodClass = 'min-w-0 [&_strong]:block [&_strong]:overflow-hidden [&_strong]:text-ellipsis [&_strong]:whitespace-nowrap';

export const testOptTableMethodLineClass =
  'text-[0.7rem] font-medium text-slate-500';

export const testOptTableMethodTypeClass =
  'block whitespace-nowrap text-[0.7rem] text-slate-400';

export const testOptTableFileClass = 'min-w-0';

export const testOptTableFileNameClass =
  'block overflow-hidden text-ellipsis whitespace-nowrap font-medium text-slate-700';

export const testOptTableFilePathClass =
  'mt-0.5 block overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[0.68rem] text-slate-400';

export const testOptTableNumClass =
  'whitespace-nowrap text-right tabular-nums';

export const testOptThNumClass = cn(
  testOptThClass,
  testOptTableNumClass,
  'overflow-visible text-clip',
);

export const testOptTdNumClass = cn(testOptTdClass, testOptTableNumClass);

export const testOptTableCoveredClass =
  'overflow-visible align-top';

export const testOptRowHoverClass = 'hover:[&>td]:bg-slate-50';

export const testOptRowFocusedClass =
  '[&>td]:bg-orange-50 [&>td]:shadow-[inset_3px_0_0_#ea580c]';

export function testOptTableRowClass(focused: boolean): string {
  return cn(testOptRowHoverClass, focused && testOptRowFocusedClass);
}

export const testOptCoveredEmptyClass = 'text-slate-400';

export const testOptCoveredListClass =
  'm-0 flex max-h-[6.5rem] list-none flex-col gap-1.5 overflow-x-hidden overflow-y-auto p-0';

export const testOptCoveredChipClass =
  'block break-words rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-[0.68rem] leading-snug text-slate-700';

/* badges */

export const testOptSeverityBaseClass =
  'inline-block rounded px-1.5 py-0.5 text-[0.65rem] font-bold tracking-wide';

const severityTone: Record<Severity, string> = {
  CRITICAL: 'bg-orange-100 text-orange-700',
  IMPORTANT: 'bg-violet-100 text-violet-700',
  LOW: 'bg-slate-100 text-slate-500',
  COVERED: 'bg-green-100 text-green-700',
  ORPHAN: 'bg-red-100 text-red-700',
  LEAF: 'bg-sky-100 text-sky-700',
};

export function testOptSeverityClass(severity: Severity | string): string {
  const key = severity as Severity;
  return cn(testOptSeverityBaseClass, severityTone[key] ?? severityTone.LOW);
}

export const testOptChangeBaseClass =
  'inline-block rounded px-1.5 py-0.5 text-[0.65rem] font-bold tracking-wider';

const changeTone: Record<'added' | 'modified' | 'deleted', string> = {
  added: 'bg-green-100 text-green-700',
  modified: 'bg-amber-100 text-amber-700',
  deleted: 'bg-red-100 text-red-700',
};

export function testOptChangeClass(
  changeType: 'added' | 'modified' | 'deleted',
): string {
  return cn(testOptChangeBaseClass, changeTone[changeType]);
}

/* ── Detail panel ───────────────────────────────────────────────── */

export const testOptDetailClass =
  'flex w-80 min-w-[280px] flex-col overflow-hidden border-l border-slate-200 bg-white';

export const testOptDetailHeaderClass =
  'border-b border-slate-200 px-4 py-3';

export const testOptDetailTitleClass =
  'm-0 text-[0.7rem] font-bold tracking-wider text-slate-400';

export const testOptDetailNameClass =
  'mt-1 break-all text-[0.95rem] font-semibold';

export const testOptDetailPathClass =
  'mt-1 break-all text-[0.7rem] text-slate-500';

export const testOptDetailChangeClass =
  'mt-1.5 mb-0 text-[0.75rem] text-slate-500';

export const testOptDetailMetricsClass =
  'grid grid-cols-2 gap-2 px-4 py-3 text-[0.75rem]';

export const testOptDetailSubtabsClass =
  'flex border-b border-slate-200';

export const testOptDetailSubtabClass =
  'flex-1 cursor-pointer border-0 bg-transparent p-2 text-[0.75rem] text-slate-500';

export const testOptDetailSubtabActiveClass =
  'border-b-2 border-orange-600 font-semibold text-orange-600';

export function testOptDetailSubtab(active: boolean): string {
  return cn(testOptDetailSubtabClass, active && testOptDetailSubtabActiveClass);
}

export const testOptDetailBodyClass =
  'flex-1 overflow-auto px-4 py-3 text-[0.75rem]';

export const testOptFooterClass =
  'flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 bg-white px-4 py-2.5';

export const testOptFooterSummaryClass = 'text-[0.8rem] text-slate-600';

export const testOptFooterActionsClass = 'flex gap-2';

/* ── Test Run Specs ─────────────────────────────────────────────── */

export const testRunSpecsClass =
  'flex min-h-0 flex-1 overflow-hidden bg-slate-50';

export const testRunSpecsLoadingClass =
  'flex flex-1 items-center justify-center p-8';

export const testRunSpecsDetailErrorClass = 'm-0 text-red-700';

export const testRunSpecsSidebarClass =
  'flex w-[280px] min-w-[240px] shrink-0 flex-col overflow-hidden border-r border-slate-200 bg-white';

export const testRunSpecsSidebarHeadClass =
  'flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-3.5 [&_h2]:m-0 [&_h2]:text-[0.8125rem] [&_h2]:font-semibold [&_h2]:uppercase [&_h2]:tracking-[0.06em] [&_h2]:text-slate-600';

export const testRunSpecsListClass =
  'min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-2.5 [scrollbar-gutter:stable]';

export const testRunSpecsEmptyHintClass =
  'm-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-3 py-4 text-center text-[0.8125rem] leading-relaxed text-slate-500';

export const testRunSpecsListItemClass =
  'group/spec-item relative mb-1.5 box-border block w-full min-w-0 shrink-0 cursor-pointer overflow-hidden rounded-xl border border-transparent bg-transparent py-2.5 pl-2 pr-3 text-left font-normal shadow-none outline-none transition-[background-color,border-color,box-shadow] duration-200 ease-out motion-reduce:transition-none last:mb-0 hover:border-slate-200/90 hover:bg-slate-50/95 hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)] focus-visible:border-orange-200 focus-visible:bg-orange-50/40 focus-visible:ring-2 focus-visible:ring-orange-500/20 focus-visible:ring-offset-2 focus-visible:ring-offset-white';

export const testRunSpecsListItemActiveClass =
  'border-orange-200/90 bg-gradient-to-br from-orange-50 via-orange-50/70 to-white shadow-[0_2px_10px_rgba(234,88,12,0.12)] hover:border-orange-200 hover:from-orange-50 hover:to-orange-50/30 hover:shadow-[0_4px_14px_rgba(234,88,12,0.14)]';

export const testRunSpecsListItemIndicatorClass =
  'pointer-events-none absolute bottom-2 left-0 top-2 w-[3px] origin-center rounded-full bg-gradient-to-b from-orange-400 to-orange-600 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none';

export const testRunSpecsListItemIndicatorIdleClass =
  'scale-y-0 opacity-0 group-hover/spec-item:scale-y-[0.55] group-hover/spec-item:opacity-35';

export const testRunSpecsListItemIndicatorActiveClass =
  'scale-y-100 opacity-100 shadow-[0_0_0_1px_rgba(234,88,12,0.15)]';

export const testRunSpecsListItemBodyClass =
  'relative flex min-w-0 flex-col gap-1 pl-2.5';

export function testRunSpecsListItem(active: boolean): string {
  return cn(
    testRunSpecsListItemClass,
    active && testRunSpecsListItemActiveClass,
  );
}

export const testRunSpecsListNameClass =
  'block min-w-0 truncate text-[0.875rem] font-semibold leading-snug text-slate-900 transition-colors duration-200 group-hover/spec-item:text-slate-950';

export const testRunSpecsListNameActiveClass = 'text-orange-950';

export const testRunSpecsListMetaClass =
  'block min-w-0 text-[0.75rem] leading-snug text-slate-500 transition-colors duration-200 group-hover/spec-item:text-slate-600';

export const testRunSpecsListMetaActiveClass = 'text-orange-900/65';

export const testRunSpecsListDateClass =
  'block min-w-0 pt-0.5 text-[0.6875rem] tabular-nums leading-snug text-slate-400 transition-colors duration-200 group-hover/spec-item:text-slate-500';

export const testRunSpecsListDateActiveClass = 'text-orange-800/50';

export const testRunSpecsMainClass =
  'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-5 py-4 pb-6';

export const testRunSpecsDetailViewClass =
  'flex min-h-0 flex-1 flex-col overflow-hidden';

export const testRunSpecsDetailScrollClass =
  'min-h-0 flex-1 overflow-y-auto overscroll-y-contain';

export const testRunSpecsEmptyMainClass =
  'mx-auto my-8 max-w-[420px] text-center text-slate-500 [&_h3]:m-0 [&_h3]:mb-2 [&_h3]:text-slate-900 [&_p]:m-0 [&_p]:mb-4 [&_p]:text-[0.85rem] [&_p]:leading-normal';

export const testRunSpecsDetailTopClass =
  'mb-3 flex shrink-0 items-center justify-between gap-4 [&_h2]:m-0 [&_h2]:text-[1.15rem] [&_h2]:font-bold [&_h2]:text-slate-900';

export const testRunSpecsDetailActionsClass = 'flex shrink-0 gap-2';

export const testRunSpecsDetailSubtitleClass =
  'mt-1 mb-0 text-[0.75rem] text-slate-500';

export const testRunSpecsStatsClass =
  'mb-4 grid shrink-0 grid-cols-4 gap-0 rounded-b-lg border border-t-0 border-slate-200 bg-white';

export const testRunSpecsStatClass =
  'border-r border-slate-200 px-2 py-3.5 text-center last:border-r-0';

export const testRunSpecsStatValueClass =
  'text-[1.35rem] font-extrabold leading-tight text-slate-900';

export const testRunSpecsStatLabelClass =
  'mt-1 text-[0.62rem] font-bold uppercase tracking-wider text-slate-500';

export function testRunSpecsStatValue(tone?: 'success' | 'danger'): string {
  return cn(
    testRunSpecsStatValueClass,
    tone === 'success' && 'text-green-700',
    tone === 'danger' && 'text-red-700',
  );
}

export const testRunSpecsTablePanelClass =
  'overflow-hidden rounded-lg border border-slate-200 bg-white';

export const testRunSpecsTablePanelExtraClass = 'mt-4';

export const testRunSpecsTableHeadClass =
  'border-b border-slate-200 bg-slate-50 px-4 py-3.5 [&_h3]:m-0 [&_h3]:text-[0.9rem] [&_h3]:font-bold [&_h3]:text-slate-900 [&_p]:mt-1 [&_p]:mb-0 [&_p]:text-[0.75rem] [&_p]:text-slate-500';

export const testRunSpecsTableWrapClass = 'bg-white';

export const testRunSpecsTableClass =
  'w-full min-w-[42rem] border-collapse text-[0.8rem]';

export const testRunSpecsColMethodClass = 'w-44 min-w-44';
export const testRunSpecsColLinesClass =
  'w-[5.5rem] min-w-[5.5rem] whitespace-nowrap';
export const testRunSpecsColTcClass = 'min-w-64';

export const testRunSpecsTableEmptyClass =
  'm-0 px-4 py-5 text-[0.85rem] text-slate-500';

export const testRunSpecsMethodNameClass =
  'font-semibold text-slate-900';

export const testRunSpecsMethodCellClass = 'min-w-44';

export const testRunSpecsTcCellClass = 'min-w-[220px] max-w-[360px]';

export const testRunSpecsTcListClass = 'flex flex-col gap-1.5';

export const testRunSpecsTcChipClass =
  'flex flex-col gap-0.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5';

export const testRunSpecsTcChipTopClass =
  'flex items-center justify-between gap-2';

export const testRunSpecsTcBadgesClass = 'flex flex-wrap gap-1';

export const testRunSpecsSourceLegendClass =
  'mt-2 flex flex-wrap gap-1.5';

export const testRunSpecsSourceBaseClass =
  'inline-block whitespace-nowrap rounded-full px-1.5 py-px text-[0.58rem] font-bold leading-snug';

const sourceTone: Record<
  'selected' | 'dependency' | 'changed' | 'failed' | 'coverage',
  string
> = {
  selected: 'bg-blue-100 text-blue-700',
  dependency: 'bg-violet-100 text-violet-800',
  changed: 'bg-orange-100 text-orange-700',
  failed: 'bg-red-100 text-red-700',
  coverage: 'bg-slate-200 text-slate-600',
};

export function testRunSpecsSourceClass(
  tone: 'selected' | 'dependency' | 'changed' | 'failed' | 'coverage',
): string {
  return cn(testRunSpecsSourceBaseClass, sourceTone[tone]);
}

export const testRunSpecsExtraTcListClass =
  'grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2 px-4 pb-4 pt-3';

export const testRunSpecsTcScriptClass =
  'break-all text-[0.62rem] leading-snug text-orange-800';

export const testRunSpecsTcIdClass =
  'text-[0.72rem] font-bold text-blue-700';

export const testRunSpecsTcNameClass =
  'break-words text-[0.68rem] font-medium leading-snug text-slate-700';

export const testRunSpecsNoTcClass =
  'text-[0.75rem] font-semibold text-red-700';

export const testRunSpecsTableRowGapClass = '[&>td]:bg-[#fffafb]';

/* ── Save Test Run Spec modal ───────────────────────────────────── */

export const saveSpecModalContentClass =
  'flex max-h-[min(90vh,720px)] max-w-[540px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[540px]';

export const saveSpecModalHeaderClass =
  'shrink-0 border-b border-slate-200 px-6 pb-4 pt-6 pr-12';

export const saveSpecModalBodyClass =
  'flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5';

export const saveSpecModalSectionClass =
  'flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4';

export const saveSpecModalFieldClass =
  'flex flex-col gap-1.5 [&_label]:m-0 [&_label]:text-[0.8125rem] [&_label]:font-semibold [&_label]:text-slate-800';

export const saveSpecModalInputClass =
  'box-border h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/15 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:opacity-70';

export const saveSpecModalMethodsLabelClass =
  'm-0 text-[0.8125rem] font-semibold text-slate-800';

export const saveSpecModalMethodsSubClass =
  'm-0 text-[0.75rem] leading-snug text-slate-500';

export const saveSpecModalPillsClass =
  'flex max-h-[132px] flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2.5';

export const saveSpecModalPillClass =
  'inline-flex max-w-full items-center truncate rounded-md border border-emerald-200/80 bg-emerald-50 px-2 py-1 text-[0.75rem] font-medium text-emerald-900';

export const saveSpecModalOptionsClass = 'flex flex-col gap-2.5';

export const saveSpecModalCheckboxClass =
  'flex cursor-pointer items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 transition-[border-color,background] duration-150 hover:border-orange-300 hover:bg-orange-50 [&_input]:mt-0.5 [&_input]:h-4 [&_input]:w-4 [&_input]:cursor-pointer [&_input]:accent-orange-600';

export const saveSpecModalCheckboxTextClass = 'flex flex-col gap-0.5';

export const saveSpecModalCheckboxTitleClass =
  'text-[0.8rem] font-semibold leading-tight text-slate-700';

export const saveSpecModalCheckboxHintClass =
  'text-[0.72rem] leading-snug text-slate-500';

export const saveSpecModalDepthPanelClass =
  'ml-[1.625rem] mt-2 flex flex-col gap-1.5 border-l-2 border-orange-200 pl-3';

export const saveSpecModalDepthHintClass =
  'm-0 text-[0.72rem] leading-snug text-slate-500';

export const saveSpecModalDepthSelectClass =
  'h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[0.75rem] font-medium text-slate-700 focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/15';

export const saveSpecModalErrorClass = 'm-0 text-[0.8rem] text-red-700';

export const saveSpecModalFooterClass =
  'shrink-0 flex justify-end gap-2 border-t border-slate-200 bg-slate-50/80 px-6 py-4';
